import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
  Icon,
  Flex,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Wrap,
  WrapItem,
  Link,
  Divider,
  Progress,
  Avatar,
  Button,
  Skeleton,
  SkeletonText,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Collapse,
  IconButton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
} from '@chakra-ui/react';
import { useCustomToast } from '../../utils/customToast';
import {
  FaBullhorn,
  FaAd,
  FaSearch,
  FaKey,
  FaUsers,
  FaChartBar,
  FaExternalLinkAlt,
  FaTrophy,
  FaMedal,
  FaAward,
  FaLightbulb,
  FaMagic,
  FaRocket,
  FaChevronDown,
  FaChevronUp,
  FaGoogle,
  FaCheckCircle,
  FaInfoCircle,
  FaEye,
  FaMousePointer,
  FaCopy,
  FaPlay,
  FaStar,
  FaArrowRight,
  FaFilter,
  FaSortAmountDown,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { SectionLabel } from './DashboardWrapper';
import {
  hasCompetitiveIntel,
  getAdHistory,
  getTotalAds,
  formatAdCount,
  getAdKeywords,
  getAvgAdPosition,
  getMostRecentAd,
  formatSearchDate,
  getTopAdKeywords,
  getAdPositionDistribution,
  getCommonCTAs,
  formatNumber,
  scoreAllAds,
  getSuccessScoreColor,
  getSuccessScoreLabel,
} from '../../utils/competitiveIntelHelpers';
import { generateIdealAd, analyzeCompetitorAds } from '../../services/aiAdService';
import { logger } from '../../utils/logger';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';
import { getBusinessImages } from '../../services/imageService';

const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green  
  '#F59E0B', // amber
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
  '#14B8A6', // teal
  '#6366F1', // indigo
  '#EF4444', // red
];

// Google Ad Preview Component
const GoogleAdPreview = ({ ad, isIdeal = false, variation = null }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const urlColor = useColorModeValue('green.700', 'green.400');
  const titleColor = useColorModeValue('blue.700', 'blue.400');
  const bodyColor = useColorModeValue('gray.700', 'gray.300');
  const sponsoredBg = useColorModeValue('yellow.100', 'yellow.900');
  const differentiatorBg = useColorModeValue('purple.50', 'purple.900');
  
  const title = isIdeal ? ad.headline : ad.title;
  const headlineExt = isIdeal ? ad.headlineExtension : '';
  const body = isIdeal ? `${ad.description1} ${ad.description2}` : ad.body;
  const displayUrl = isIdeal ? 'www.yourbusiness.com' : (ad.url || 'www.example.com');
  
  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={isIdeal ? 'purple.300' : borderColor}
      borderRadius="lg"
      p={4}
      position="relative"
      shadow={isIdeal ? 'lg' : 'sm'}
      _hover={{ shadow: 'md', borderColor: isIdeal ? 'purple.400' : 'blue.300' }}
      transition="all 0.2s"
    >
      {isIdeal && (
        <Badge
          position="absolute"
          top={-2}
          right={2}
          colorScheme="purple"
          variant="solid"
          fontSize="xs"
        >
          <Icon as={FaMagic} mr={1} /> AI Generated - {variation}
        </Badge>
      )}
      
      {/* Sponsored label */}
      <HStack spacing={2} mb={2}>
        <Badge bg={sponsoredBg} color="gray.700" fontSize="xs" px={2} py={0.5} borderRadius="sm">
          Sponsored
        </Badge>
      </HStack>
      
      {/* URL Line */}
      <HStack spacing={1} mb={1}>
        <Icon as={FaGoogle} color="gray.400" boxSize={3} />
        <Text fontSize="sm" color={urlColor} noOfLines={1}>
          {displayUrl.replace(/^https?:\/\//, '').split('/')[0]}
        </Text>
      </HStack>
      
      {/* Title */}
      <Text
        fontSize="lg"
        fontWeight="semibold"
        color={titleColor}
        mb={1}
        noOfLines={2}
        _hover={{ textDecoration: 'underline' }}
        cursor="pointer"
      >
        {title}{headlineExt ? ` | ${headlineExt}` : ''}
      </Text>
      
      {/* Body */}
      <Text fontSize="sm" color={bodyColor} noOfLines={3} mb={2}>
        {body}
      </Text>
      
      {/* Keywords or CTA */}
      <HStack spacing={2} flexWrap="wrap">
        {isIdeal ? (
          <>
            <Tag size="sm" colorScheme="green" variant="subtle">
              <TagLeftIcon as={FaMousePointer} />
              <TagLabel>{ad.cta}</TagLabel>
            </Tag>
            <Tag size="sm" colorScheme="purple" variant="subtle">
              <TagLeftIcon as={FaLightbulb} />
              <TagLabel>{ad.targetEmotion}</TagLabel>
            </Tag>
          </>
        ) : (
          ad.keywords?.slice(0, 3).map((kw, idx) => (
            <Tag key={idx} size="sm" colorScheme="blue" variant="subtle">
              {kw}
            </Tag>
          ))
        )}
      </HStack>
      
      {/* Business name for competitor ads */}
      {!isIdeal && ad.businessName && (
        <Text fontSize="xs" color="gray.500" mt={2}>
          <Icon as={FaUsers} mr={1} /> {ad.businessName}
        </Text>
      )}
      
      {/* Differentiator for ideal ads */}
      {isIdeal && ad.differentiator && (
        <Box mt={3} p={2} bg={differentiatorBg} borderRadius="md">
          <Text fontSize="xs" color="purple.600" fontWeight="medium">
            <Icon as={FaStar} mr={1} /> {ad.differentiator}
          </Text>
        </Box>
      )}
    </Box>
  );
};

// Top Advertiser Card Component
const TopAdvertiserCard = ({ advertiser, rank, totalAdvertisers, avgAds, onClick }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  const getRankIcon = (rank) => {
    switch(rank) {
      case 1: return { icon: FaTrophy, color: 'yellow.400' };
      case 2: return { icon: FaMedal, color: 'gray.400' };
      case 3: return { icon: FaAward, color: 'orange.400' };
      default: return { icon: FaAd, color: 'blue.400' };
    }
  };

  const { icon, color } = getRankIcon(rank);

  const diffFromAvg = avgAds && avgAds > 0 ? advertiser.adCount - avgAds : null;
  const diffPct = avgAds && avgAds > 0
    ? ((advertiser.adCount - avgAds) / avgAds) * 100
    : null;

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      shadow="sm"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)', cursor: 'pointer' }}
      transition="all 0.2s"
      onClick={onClick}
    >
      <CardBody>
        <HStack spacing={4}>
          <Flex
            w="50px"
            h="50px"
            bg={statBg}
            borderRadius="full"
            justify="center"
            align="center"
          >
            <Icon as={icon} color={color} boxSize={6} />
          </Flex>

          <VStack align="start" spacing={1} flex={1}>
            <HStack>
              <Badge colorScheme={rank <= 3 ? 'yellow' : 'gray'} fontSize="xs">
                #{rank}{totalAdvertisers ? ` of ${totalAdvertisers}` : ''}
              </Badge>
              <Text fontWeight="bold" noOfLines={1}>{advertiser.name}</Text>
            </HStack>

            <HStack spacing={4} fontSize="sm" color="gray.500" flexWrap="wrap">
              <HStack spacing={1}>
                <Icon as={FaAd} boxSize={3} />
                <Text fontWeight="semibold" color="gray.700">
                  {formatAdCount(advertiser.adCount)} ads
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Icon as={FaKey} boxSize={3} />
                <Text>{advertiser.keywordCount} keywords</Text>
              </HStack>
              {diffFromAvg !== null && (
                <HStack spacing={1}>
                  <Text fontSize="xs" color={diffFromAvg >= 0 ? 'green.500' : 'red.500'} fontWeight="semibold">
                    {diffFromAvg >= 0 ? '▲' : '▼'} {Math.abs(diffPct).toFixed(0)}% vs avg
                  </Text>
                </HStack>
              )}
            </HStack>
          </VStack>

          <VStack align="end" spacing={1}>
            <Badge colorScheme="green" fontSize="md" px={2}>
              #{advertiser.avgPosition.toFixed(1)}
            </Badge>
            <Text fontSize="xs" color="gray.500">Avg Position</Text>
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

// AI Ideal Ad Generator Component
const IdealAdGenerator = ({ businessesWithAds, allAds, topKeywords, vertical }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [idealAdData, setIdealAdData] = useState(null);
  const [error, setError] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [useSuccessScoring, setUseSuccessScoring] = useState(true);
  const [minSuccessScore, setMinSuccessScore] = useState(60);
  const toast = useCustomToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const gradientBg = useColorModeValue(
    'linear(to-r, purple.50, blue.50, pink.50)',
    'linear(to-r, purple.900, blue.900, pink.900)'
  );
  const sliderMarkBg = useColorModeValue('gray.100', 'gray.600');

  // Calculate ad scoring summary
  const adScoringSummary = useMemo(() => {
    return scoreAllAds(businessesWithAds);
  }, [businessesWithAds]);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);

    try {
      // Prepare competitor ads data - get top ads by position
      const topAds = allAds
        .filter(ad => ad.title && ad.body)
        .sort((a, b) => (a.position || 99) - (b.position || 99))
        .slice(0, 50) // Get more ads to allow for filtering
        .map(ad => ({
          title: ad.title,
          body: ad.body,
          url: ad.url,
          keywords: ad.keywords || [],
          position: ad.position,
          businessName: ad.businessName,
          searchDateId: ad.searchDateId,
        }));

      if (topAds.length < 3) {
        throw new Error('Need at least 3 competitor ads to generate an ideal ad');
      }

      // Get target keywords
      const targetKws = topKeywords.slice(0, 10).map(kw => kw.keyword);

      const result = await generateIdealAd(
        vertical || 'general',
        topAds,
        targetKws,
        `A business competing in the ${vertical || 'local services'} market`,
        useSuccessScoring,
        minSuccessScore
      );

      if (result.success && result.data) {
        setIdealAdData(result.data);
        const scoringMsg = result.data.successScoringUsed
          ? `Used ${result.data.successfulAdsCount} successful ads out of ${result.data.totalAdsAnalyzed} analyzed.`
          : 'Used top ads by position (not enough successful ads found).';
        toast({
          title: 'Ideal Ads Generated!',
          description: `AI has analyzed competitor ads and created optimized variations. ${scoringMsg}`,
          status: 'success',
          duration: 6000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error || 'Failed to generate ideal ad');
      }
    } catch (err) {
      logger.error('Error generating ideal ad:', err);
      setError(err.message);
      toast({
        title: 'Generation Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGenerating(false);
    }
  }, [allAds, topKeywords, vertical, toast, useSuccessScoring, minSuccessScore]);
  
  const copyAdToClipboard = (ad) => {
    const text = `Headline: ${ad.headline}
Headline Extension: ${ad.headlineExtension}
Description 1: ${ad.description1}
Description 2: ${ad.description2}
CTA: ${ad.cta}`;
    
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard!',
      status: 'success',
      duration: 2000,
    });
  };
  
  return (
    <Box
      bgGradient={gradientBg}
      borderWidth="2px"
      borderColor="purple.300"
      borderRadius="xl"
      p={6}
      mb={8}
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <HStack spacing={3}>
            <Flex
              w="48px"
              h="48px"
              bg="purple.500"
              borderRadius="lg"
              justify="center"
              align="center"
            >
              <Icon as={FaMagic} color="white" boxSize={6} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="purple.700">
                AI-Powered Ideal Ad Generator
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Create optimized ads based on competitor analysis
              </Text>
            </VStack>
          </HStack>
          
          <Button
            colorScheme="purple"
            size="lg"
            leftIcon={<Icon as={isGenerating ? FaRocket : FaLightbulb} />}
            onClick={handleGenerate}
            isLoading={isGenerating}
            loadingText="Analyzing & Generating..."
            isDisabled={allAds.length < 3}
          >
            Generate Ideal Ads
          </Button>
        </Flex>
        
        {/* Info alert */}
        {!idealAdData && !isGenerating && (
          <Alert status="info" borderRadius="lg" bg="blue.50">
            <AlertIcon />
            <Box>
              <AlertTitle>How it works</AlertTitle>
              <AlertDescription>
                Our AI analyzes your competitors' most successful ads to identify proven winning patterns,
                CTAs, and messaging strategies. It then generates 3 unique ad variations tailored to outperform them.
              </AlertDescription>
            </Box>
          </Alert>
        )}
        
        {/* Error display */}
        {error && (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Loading skeleton */}
        {isGenerating && (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {[1, 2, 3].map(i => (
              <Card key={i} bg={cardBg}>
                <CardBody>
                  <Skeleton height="20px" mb={3} />
                  <Skeleton height="16px" mb={2} />
                  <SkeletonText noOfLines={3} spacing={2} />
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        )}
        
        {/* Generated ads */}
        {idealAdData && !isGenerating && (
          <VStack spacing={6} align="stretch">
            {/* Analysis Section */}
            <Box>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAnalysis(!showAnalysis)}
                rightIcon={<Icon as={showAnalysis ? FaChevronUp : FaChevronDown} />}
                color="purple.600"
              >
                {showAnalysis ? 'Hide' : 'Show'} Competitor Analysis
              </Button>
              
              <Collapse in={showAnalysis}>
                <Box mt={4} p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" fontSize="sm" color="purple.600">
                        <Icon as={FaChartBar} mr={2} />
                        Competitor Patterns
                      </Text>
                      {idealAdData.analysis?.competitorPatterns?.map((pattern, i) => (
                        <Text key={i} fontSize="sm">• {pattern}</Text>
                      ))}
                    </VStack>
                    
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" fontSize="sm" color="green.600">
                        <Icon as={FaMousePointer} mr={2} />
                        Common CTAs
                      </Text>
                      {idealAdData.analysis?.commonCTAs?.map((cta, i) => (
                        <Badge key={i} colorScheme="green" variant="subtle">{cta}</Badge>
                      ))}
                    </VStack>
                    
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" fontSize="sm" color="blue.600">
                        <Icon as={FaCheckCircle} mr={2} />
                        Effective Elements
                      </Text>
                      {idealAdData.analysis?.effectiveElements?.map((elem, i) => (
                        <Text key={i} fontSize="sm">• {elem}</Text>
                      ))}
                    </VStack>
                    
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" fontSize="sm" color="orange.600">
                        <Icon as={FaLightbulb} mr={2} />
                        Gaps to Exploit
                      </Text>
                      {idealAdData.analysis?.gapsToExploit?.map((gap, i) => (
                        <Text key={i} fontSize="sm">• {gap}</Text>
                      ))}
                    </VStack>
                  </SimpleGrid>
                </Box>
              </Collapse>
            </Box>
            
            {/* Success Scoring Summary */}
            {idealAdData.successScoringUsed !== undefined && (
              <Box p={3} bg={idealAdData.successScoringUsed ? 'green.50' : 'yellow.50'} borderRadius="md">
                <HStack spacing={4} flexWrap="wrap">
                  <Badge colorScheme={idealAdData.successScoringUsed ? 'green' : 'yellow'} fontSize="sm">
                    {idealAdData.successScoringUsed ? 'Success Scoring Applied' : 'Fallback Mode'}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    Used {idealAdData.successfulAdsCount} of {idealAdData.totalAdsAnalyzed} ads
                    {idealAdData.minScoreThreshold && ` (min score: ${idealAdData.minScoreThreshold})`}
                  </Text>
                </HStack>
              </Box>
            )}

            {/* Generated Ad Variations */}
            <Box>
              <Heading size="sm" mb={4} color="purple.700">
                <Icon as={FaStar} mr={2} />
                Generated Ad Variations
              </Heading>
              
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {idealAdData.idealAds?.map((ad, index) => (
                  <Box key={index} position="relative">
                    <GoogleAdPreview
                      ad={ad}
                      isIdeal={true}
                      variation={ad.variation}
                    />
                    <IconButton
                      icon={<FaCopy />}
                      size="sm"
                      colorScheme="purple"
                      variant="ghost"
                      position="absolute"
                      bottom={2}
                      right={2}
                      onClick={() => copyAdToClipboard(ad)}
                      aria-label="Copy ad"
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            
            {/* Recommendations */}
            {idealAdData.recommendations?.length > 0 && (
              <Box p={4} bg="green.50" borderRadius="lg">
                <Heading size="sm" mb={3} color="green.700">
                  <Icon as={FaLightbulb} mr={2} />
                  AI Recommendations
                </Heading>
                <VStack align="start" spacing={2}>
                  {idealAdData.recommendations.map((rec, i) => (
                    <HStack key={i} align="start">
                      <Icon as={FaArrowRight} color="green.500" mt={1} />
                      <Text fontSize="sm">{rec}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

// Main AdsDashboard Component
const AdsDashboard = ({ reviewsData = [], demoMode, demoData }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [keywordFilter, setKeywordFilter] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [businessImages, setBusinessImages] = useState({});
  const [showAllAdvertisers, setShowAllAdvertisers] = useState(false);
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  // Custom label renderer for pie charts that shows spinning stars for favorited businesses
  const FavoriteStarPieLabel = useMemo(() => {
    return createFavoriteStarPieLabelRenderer({
      businessNameToUriMap,
      favoriteUris
    });
  }, [businessNameToUriMap, favoriteUris]);

  // Filter businesses with ad history
  const businessesWithAds = useMemo(() => {
    return reviewsData.filter(business => {
      const ads = getAdHistory(business);
      return ads.length > 0;
    });
  }, [reviewsData]);

  // Detect vertical from review data
  const vertical = useMemo(() => {
    if (!reviewsData.length) return 'general';
    const types = reviewsData
      .map(b => b.primary_type || b.category)
      .filter(Boolean)
      .map(t => t.toLowerCase());
    
    // Simple detection
    if (types.some(t => t.includes('plumb'))) return 'plumbing';
    if (types.some(t => t.includes('electric'))) return 'electrical';
    if (types.some(t => t.includes('hvac') || t.includes('heating'))) return 'hvac';
    if (types.some(t => t.includes('dental') || t.includes('dentist'))) return 'dental';
    if (types.some(t => t.includes('restaurant') || t.includes('food'))) return 'restaurant';
    if (types.some(t => t.includes('auto') || t.includes('car'))) return 'automotive';
    return 'home_services';
  }, [reviewsData]);

  // Calculate aggregate statistics
  const stats = useMemo(() => {
    if (!businessesWithAds.length) return null;

    const totalAds = businessesWithAds.reduce((sum, b) => sum + getTotalAds(b), 0);
    const allKeywords = new Set();
    let totalPositionSum = 0;
    let adsWithPosition = 0;
    let topPositionCount = 0;

    businessesWithAds.forEach(business => {
      const keywords = getAdKeywords(business);
      keywords.forEach(kw => allKeywords.add(kw));
      
      const ads = getAdHistory(business);
      ads.forEach(ad => {
        if (ad.position) {
          totalPositionSum += ad.position;
          adsWithPosition++;
          if (ad.position <= 3) topPositionCount++;
        }
      });
    });

    const avgPosition = adsWithPosition > 0 ? totalPositionSum / adsWithPosition : 0;
    const topPositionRate = adsWithPosition > 0 ? (topPositionCount / adsWithPosition * 100) : 0;

    return {
      totalBusinesses: businessesWithAds.length,
      totalAds,
      totalKeywords: allKeywords.size,
      avgPosition: avgPosition.toFixed(2),
      avgAdsPerBusiness: (totalAds / businessesWithAds.length).toFixed(1),
      topPositionRate: topPositionRate.toFixed(1),
      marketCoverage: ((businessesWithAds.length / reviewsData.length) * 100).toFixed(1),
    };
  }, [businessesWithAds, reviewsData.length]);

  // Full ranking of advertisers by ad count (descending)
  const allAdvertisersRanked = useMemo(() => {
    return [...businessesWithAds]
      .sort((a, b) => getTotalAds(b) - getTotalAds(a))
      .map((business, idx) => ({
        rank: idx + 1,
        name: business.name,
        adCount: getTotalAds(business),
        keywordCount: getAdKeywords(business).length,
        avgPosition: getAvgAdPosition(business),
        mostRecentAd: getMostRecentAd(business),
        business,
      }));
  }, [businessesWithAds]);

  // Top advertisers (top 10 by ad count)
  const topAdvertisers = useMemo(() => {
    return allAdvertisersRanked.slice(0, 10);
  }, [allAdvertisersRanked]);

  // Average ads per advertiser (only counts businesses with ads)
  const avgAdsPerAdvertiser = useMemo(() => {
    if (!allAdvertisersRanked.length) return 0;
    const total = allAdvertisersRanked.reduce(
      (sum, adv) => sum + adv.adCount,
      0,
    );
    return total / allAdvertisersRanked.length;
  }, [allAdvertisersRanked]);

  // Get all ads with business info
  const allAds = useMemo(() => {
    const ads = [];
    businessesWithAds.forEach(business => {
      const businessAds = getAdHistory(business);
      businessAds.forEach(ad => {
        ads.push({
          ...ad,
          businessName: business.name,
          businessDomain: business.spyfu_data?.domain || '',
        });
      });
    });
    return ads.sort((a, b) => (a.position || 99) - (b.position || 99));
  }, [businessesWithAds]);

  // Top keywords
  const topKeywords = useMemo(() => {
    return getTopAdKeywords(businessesWithAds, 20);
  }, [businessesWithAds]);

  // Position distribution for chart
  const positionDistribution = useMemo(() => {
    const dist = getAdPositionDistribution(businessesWithAds);
    return Object.entries(dist)
      .map(([position, count]) => ({
        position: `#${position}`,
        count,
        fill: COLORS[parseInt(position) - 1] || COLORS[9],
      }))
      .sort((a, b) => {
        const posA = parseInt(a.position.replace('#', ''));
        const posB = parseInt(b.position.replace('#', ''));
        return posA - posB;
      })
      .slice(0, 10);
  }, [businessesWithAds]);

  // Ad count by business for chart
  const adCountByBusiness = useMemo(() => {
    return topAdvertisers.slice(0, 8).map((adv, idx) => ({
      name: adv.name.split(' ').slice(0, 2).join(' '),
      fullName: adv.name,
      ads: adv.adCount,
      keywords: adv.keywordCount,
      fill: COLORS[idx % COLORS.length],
    }));
  }, [topAdvertisers]);

  // Common CTAs
  const commonCTAs = useMemo(() => {
    return getCommonCTAs(businessesWithAds).slice(0, 8);
  }, [businessesWithAds]);

  // Filtered ads for display
  const filteredAds = useMemo(() => {
    let filtered = allAds;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ad =>
        ad.businessName?.toLowerCase().includes(query) ||
        ad.title?.toLowerCase().includes(query) ||
        ad.body?.toLowerCase().includes(query)
      );
    }
    
    if (keywordFilter) {
      const keywordQuery = keywordFilter.toLowerCase();
      filtered = filtered.filter(ad =>
        ad.keywords?.some(kw => kw.toLowerCase().includes(keywordQuery))
      );
    }
    
    return filtered;
  }, [allAds, searchQuery, keywordFilter]);

  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
      }
    };
    fetchImages();
    return () => { mounted = false; };
  }, []);

  if (!stats || stats.totalBusinesses === 0) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="Discover what marketing channels and ad strategies your competitors are using. Stay ahead of the local advertising landscape.">
          Google Ads
        </SectionLabel>}
        
        <Box p={12} textAlign="center">
          <VStack spacing={6}>
            <Flex
              w="120px"
              h="120px"
              bg="gray.100"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaBullhorn} boxSize={16} color="gray.400" />
            </Flex>
            <Heading size="lg" color="gray.500">
              No Advertising Data Available
            </Heading>
            <Text color="gray.500" maxW="500px">
              Historical advertising data will appear here once businesses have been enriched with
              ad history data. Check back soon to analyze competitor advertising strategies.
            </Text>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Top 3 Advertisers Podium - Only show on desktop and tablet */}
      {topAdvertisers.length >= 3 && (
        <Box
          display={{ base: 'none', md: 'block' }}
          bg='transparent'
          mb={8}
        >
          <Flex justify='center' align='flex-end' gap={6} w='full'>
            {/* Silver - Second Place */}
            <Box
              bg={cardBg}
              borderRadius='2xl'
              textAlign='center'
              position='relative'
              transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              _hover={{ 
                transform: 'translateY(-8px)', 
                shadow: '2xl',
              }}
              overflow='hidden'
              flex='1'
              maxW='400px'
              boxShadow='xl'
              borderWidth='1px'
              borderColor={borderColor}
            >
              <Box h='4px' bgGradient='linear(to-r, gray.300, gray.500, gray.300)' />
              <VStack p={6} spacing={4}>
                <Flex
                  position='absolute'
                  top={4}
                  left={4}
                  minW='36px'
                  h='36px'
                  px={2}
                  borderRadius='full'
                  bgGradient='linear(to-br, gray.300, gray.500)'
                  justify='center'
                  align='center'
                  shadow='md'
                >
                  <Text color='white' fontWeight='bold' fontSize='md' lineHeight='1'>2</Text>
                </Flex>
                
                <Icon as={FaMedal} color='gray.400' boxSize={6} />
                
                <Box p='3px' borderRadius='full' bgGradient='linear(to-br, gray.300, gray.500)'>
                  <Avatar 
                    name={topAdvertisers[1]?.name} 
                    src={businessImages[topAdvertisers[1]?.business?.maps_uri] || ''} 
                    bg='gray.100' 
                    boxSize='70px'
                    borderWidth='3px'
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={topAdvertisers[1]?.name} placement='top' hasArrow openDelay={300}>
                    <Heading size='sm' noOfLines={1} color='gray.700'>{topAdvertisers[1]?.name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight='extrabold' 
                    fontSize='2xl' 
                    bgGradient='linear(to-r, gray.600, gray.800)'
                    bgClip='text'
                  >
                    {formatAdCount(topAdvertisers[1]?.adCount)}
                  </Text>
                  <Text fontSize='xs' color='gray.500' fontWeight='medium' textTransform='uppercase' letterSpacing='wider'>
                    total ads
                  </Text>
                </VStack>
                
                <Box w='full' px={2}>
                  <Box h='6px' bg='gray.100' borderRadius='full' overflow='hidden'>
                    <Box
                      h='full'
                      w={`${(topAdvertisers[1]?.adCount / (topAdvertisers[0]?.adCount || 1)) * 100}%`}
                      bgGradient='linear(to-r, gray.400, gray.500)'
                      borderRadius='full'
                      transition='width 0.5s ease-out'
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Gold - First Place */}
            <Box
              bg={cardBg}
              borderRadius='2xl'
              textAlign='center'
              position='relative'
              transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              _hover={{ 
                transform: 'translateY(-12px) scale(1.02)', 
                shadow: '0 25px 50px -12px rgba(251, 191, 36, 0.4)',
              }}
              overflow='hidden'
              flex='1.15'
              maxW='450px'
              boxShadow='0 20px 40px -12px rgba(251, 191, 36, 0.3)'
              zIndex={2}
              mb={4}
              borderWidth='1px'
              borderColor='yellow.200'
            >
              <Box
                h='6px'
                bgGradient='linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)'
                backgroundSize='200% 100%'
                animation='shimmer 2s linear infinite'
                sx={{
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
              <VStack p={8} spacing={4}>
                <Flex
                  position='absolute'
                  top={4}
                  left={4}
                  minW='40px'
                  h='40px'
                  px={2}
                  borderRadius='full'
                  bgGradient='linear(to-br, yellow.300, orange.400)'
                  justify='center'
                  align='center'
                  shadow='lg'
                >
                  <Text color='white' fontWeight='bold' fontSize='lg' lineHeight='1'>1</Text>
                </Flex>
                
                <Icon as={FaTrophy} color='yellow.400' boxSize={10} filter='drop-shadow(0 2px 4px rgba(251, 191, 36, 0.5))' />
                
                <Box p='4px' borderRadius='full' bgGradient='linear(to-br, yellow.300, orange.400, yellow.500)' shadow='lg'>
                  <Avatar 
                    name={topAdvertisers[0]?.name} 
                    src={businessImages[topAdvertisers[0]?.business?.maps_uri] || ''} 
                    bg='yellow.50' 
                    boxSize='90px'
                    borderWidth='4px'
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={topAdvertisers[0]?.name} placement='top' hasArrow openDelay={300}>
                    <Heading size='md' noOfLines={1} color='gray.800'>{topAdvertisers[0]?.name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight='extrabold' 
                    fontSize='3xl' 
                    bgGradient='linear(to-r, yellow.500, orange.500)'
                    bgClip='text'
                  >
                    {formatAdCount(topAdvertisers[0]?.adCount)}
                  </Text>
                  <Text fontSize='xs' color='gray.500' fontWeight='medium' textTransform='uppercase' letterSpacing='wider'>
                    total ads
                  </Text>
                </VStack>
                
                <Box w='full' px={2}>
                  <Box h='8px' bg='yellow.50' borderRadius='full' overflow='hidden'>
                    <Box h='full' w='100%' bgGradient='linear(to-r, yellow.400, orange.400)' borderRadius='full' />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Bronze - Third Place */}
            <Box
              bg={cardBg}
              borderRadius='2xl'
              textAlign='center'
              position='relative'
              transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              _hover={{ 
                transform: 'translateY(-8px)', 
                shadow: '2xl',
              }}
              overflow='hidden'
              flex='1'
              maxW='400px'
              boxShadow='xl'
              borderWidth='1px'
              borderColor={borderColor}
            >
              <Box h='4px' bgGradient='linear(to-r, orange.200, orange.400, orange.200)' />
              <VStack p={6} spacing={4}>
                <Flex
                  position='absolute'
                  top={4}
                  left={4}
                  minW='36px'
                  h='36px'
                  px={2}
                  borderRadius='full'
                  bgGradient='linear(to-br, orange.300, orange.500)'
                  justify='center'
                  align='center'
                  shadow='md'
                >
                  <Text color='white' fontWeight='bold' fontSize='md' lineHeight='1'>3</Text>
                </Flex>
                
                <Icon as={FaAward} color='orange.400' boxSize={6} />
                
                <Box p='3px' borderRadius='full' bgGradient='linear(to-br, orange.300, orange.500)'>
                  <Avatar 
                    name={topAdvertisers[2]?.name} 
                    src={businessImages[topAdvertisers[2]?.business?.maps_uri] || ''} 
                    bg='orange.50' 
                    boxSize='70px'
                    borderWidth='3px'
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={topAdvertisers[2]?.name} placement='top' hasArrow openDelay={300}>
                    <Heading size='sm' noOfLines={1} color='gray.700'>{topAdvertisers[2]?.name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight='extrabold' 
                    fontSize='2xl' 
                    bgGradient='linear(to-r, orange.500, orange.600)'
                    bgClip='text'
                  >
                    {formatAdCount(topAdvertisers[2]?.adCount)}
                  </Text>
                  <Text fontSize='xs' color='gray.500' fontWeight='medium' textTransform='uppercase' letterSpacing='wider'>
                    total ads
                  </Text>
                </VStack>
                
                <Box w='full' px={2}>
                  <Box h='6px' bg='orange.50' borderRadius='full' overflow='hidden'>
                    <Box
                      h='full'
                      w={`${(topAdvertisers[2]?.adCount / (topAdvertisers[0]?.adCount || 1)) * 100}%`}
                      bgGradient='linear(to-r, orange.300, orange.500)'
                      borderRadius='full'
                      transition='width 0.5s ease-out'
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {!demoMode && <SectionLabel description="Discover what marketing channels and ad strategies your competitors are using. Stay ahead of the local advertising landscape.">
        Google Ads
      </SectionLabel>}

      {/* Hero Stats Section - Bubble Cards */}
      <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={4} mb={8}>
        {/* Advertisers Bubble */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={5}
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          textAlign="center"
          _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <Flex justify="center" mb={3}>
            <Flex
              w="40px"
              h="40px"
              bg="blue.50"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaUsers} color="blue.500" boxSize={4} />
            </Flex>
          </Flex>
          <Text fontSize="sm" color={subtitleColor} fontWeight="medium" mb={1}>
            Advertisers
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="blue.600" mb={1}>
            {stats.totalBusinesses}
          </Text>
          <Text fontSize="xs" color="green.500">
            ▲ {stats.marketCoverage}% of market
          </Text>
        </Box>

        {/* Total Ads Bubble */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={5}
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          textAlign="center"
          _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <Flex justify="center" mb={3}>
            <Flex
              w="40px"
              h="40px"
              bg="purple.50"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaAd} color="purple.500" boxSize={4} />
            </Flex>
          </Flex>
          <Text fontSize="sm" color={subtitleColor} fontWeight="medium" mb={1}>
            Total Ads
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="purple.600" mb={1}>
            {formatNumber(stats.totalAds)}
          </Text>
          <Text fontSize="xs" color={subtitleColor}>{stats.avgAdsPerBusiness} per business</Text>
        </Box>

        {/* Keywords Bubble */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={5}
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          textAlign="center"
          _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <Flex justify="center" mb={3}>
            <Flex
              w="40px"
              h="40px"
              bg="green.50"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaKey} color="green.500" boxSize={4} />
            </Flex>
          </Flex>
          <Text fontSize="sm" color={subtitleColor} fontWeight="medium" mb={1}>
            Keywords
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="green.600" mb={1}>
            {formatNumber(stats.totalKeywords)}
          </Text>
          <Text fontSize="xs" color={subtitleColor}>Unique terms</Text>
        </Box>

        {/* Avg Position Bubble */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={5}
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          textAlign="center"
          _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <Flex justify="center" mb={3}>
            <Flex
              w="40px"
              h="40px"
              bg="orange.50"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaTrophy} color="orange.500" boxSize={4} />
            </Flex>
          </Flex>
          <Text fontSize="sm" color={subtitleColor} fontWeight="medium" mb={1}>
            Avg Position
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="orange.600" mb={1}>
            #{stats.avgPosition}
          </Text>
          <Text fontSize="xs" color={subtitleColor}>Across all ads</Text>
        </Box>

        {/* Top 3 Rate Bubble */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={5}
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          textAlign="center"
          _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <Flex justify="center" mb={3}>
            <Flex
              w="40px"
              h="40px"
              bg="yellow.50"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaStar} color="yellow.500" boxSize={4} />
            </Flex>
          </Flex>
          <Text fontSize="sm" color={subtitleColor} fontWeight="medium" mb={1}>
            Top 3 Rate
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="yellow.600" mb={1}>
            {stats.topPositionRate}%
          </Text>
          <Text fontSize="xs" color={subtitleColor}>In positions 1-3</Text>
        </Box>

        {/* Visibility Bubble */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={5}
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          textAlign="center"
          _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <Flex justify="center" mb={3}>
            <Flex
              w="40px"
              h="40px"
              bg="cyan.50"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaEye} color="cyan.500" boxSize={4} />
            </Flex>
          </Flex>
          <Text fontSize="sm" color={subtitleColor} fontWeight="medium" mb={1}>
            Visibility
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="cyan.600" mb={1}>
            High
          </Text>
          <Text fontSize="xs" color={subtitleColor}>Market presence</Text>
        </Box>

        {/* Competition Bubble */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={5}
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          textAlign="center"
          _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <Flex justify="center" mb={3}>
            <Flex
              w="40px"
              h="40px"
              bg="red.50"
              borderRadius="full"
              justify="center"
              align="center"
            >
              <Icon as={FaChartBar} color="red.500" boxSize={4} />
            </Flex>
          </Flex>
          <Text fontSize="sm" color={subtitleColor} fontWeight="medium" mb={1}>
            Competition
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="red.500" mb={1}>
            {stats.totalAds > 100 ? 'High' : stats.totalAds > 30 ? 'Medium' : 'Low'}
          </Text>
          <Text fontSize="xs" color={subtitleColor}>Ad density level</Text>
        </Box>
      </SimpleGrid>

      {/* AI Ideal Ad Generator Section */}
      <IdealAdGenerator
        businessesWithAds={businessesWithAds}
        allAds={allAds}
        topKeywords={topKeywords}
        vertical={vertical}
      />

      {/* Main Content Tabs */}
      <Tabs variant="soft-rounded" colorScheme="blue" onChange={setSelectedTab} mb={8}>
        <TabList flexWrap="wrap" gap={2}>
          <Tab>
            <Icon as={FaTrophy} mr={2} />
            Top Competitors
          </Tab>
          <Tab>
            <Icon as={FaGoogle} mr={2} />
            Ad Gallery
          </Tab>
          <Tab>
            <Icon as={FaKey} mr={2} />
            Keywords
          </Tab>
          <Tab>
            <Icon as={FaChartBar} mr={2} />
            Analytics
          </Tab>
        </TabList>

        <TabPanels>
          {/* Top Competitors Tab */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Top Advertisers List */}
              <VStack spacing={4} align="stretch">
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <Heading size="md" color={textColor}>
                    <Icon as={FaTrophy} mr={2} color="yellow.500" />
                    Advertisers Ranked by Ads Running
                  </Heading>
                  <HStack spacing={2}>
                    <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
                      Avg {avgAdsPerAdvertiser >= 10 ? avgAdsPerAdvertiser.toFixed(0) : avgAdsPerAdvertiser.toFixed(1)} ads
                    </Badge>
                    <Badge colorScheme="purple" fontSize="sm" px={2} py={1}>
                      {allAdvertisersRanked.length} advertisers
                    </Badge>
                  </HStack>
                </Flex>
                <Text fontSize="sm" color={subtitleColor}>
                  Each card shows the business&apos;s rank by total ads running and how it compares to the market average.
                </Text>
                {(showAllAdvertisers
                  ? allAdvertisersRanked
                  : topAdvertisers
                ).map((advertiser, index) => (
                  <TopAdvertiserCard
                    key={advertiser.name + index}
                    advertiser={advertiser}
                    rank={advertiser.rank ?? index + 1}
                    totalAdvertisers={allAdvertisersRanked.length}
                    avgAds={avgAdsPerAdvertiser}
                  />
                ))}
                {allAdvertisersRanked.length > topAdvertisers.length && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    rightIcon={<Icon as={showAllAdvertisers ? FaChevronUp : FaChevronDown} />}
                    onClick={() => setShowAllAdvertisers((prev) => !prev)}
                  >
                    {showAllAdvertisers
                      ? `Show top 10`
                      : `Show all ${allAdvertisersRanked.length} advertisers`}
                  </Button>
                )}
              </VStack>

              {/* Competitor Comparison - Horizontal Bar Chart */}
              <Card bg={cardBg} shadow="md">
                <CardHeader pb={2}>
                  <Heading size="md" color={textColor}>
                    <Icon as={FaChartBar} mr={2} color="purple.500" />
                    Top 3 Competitor Breakdown
                  </Heading>
                  <Text fontSize="sm" color={subtitleColor} mt={1}>
                    Compare ad volume, keyword coverage, and average position
                  </Text>
                </CardHeader>
                <CardBody>
                  {topAdvertisers.length >= 3 ? (
                    <VStack spacing={6} align="stretch">
                      {topAdvertisers.slice(0, 3).map((adv, idx) => {
                        const maxAds = Math.max(...topAdvertisers.slice(0, 3).map(a => a.adCount));
                        const maxKeywords = Math.max(...topAdvertisers.slice(0, 3).map(a => a.keywordCount));
                        const adPercent = (adv.adCount / maxAds) * 100;
                        const kwPercent = (adv.keywordCount / maxKeywords) * 100;
                        
                        return (
                          <Box key={adv.name} p={4} bg={statBg} borderRadius="lg" borderLeft="4px solid" borderLeftColor={COLORS[idx]}>
                            <HStack justify="space-between" mb={3}>
                              <HStack spacing={2}>
                                <Badge colorScheme={idx === 0 ? 'yellow' : idx === 1 ? 'gray' : 'orange'} fontSize="sm">
                                  #{idx + 1}
                                </Badge>
                                <Text fontWeight="bold" color={textColor} noOfLines={1} maxW="200px">
                                  {adv.name}
                                </Text>
                              </HStack>
                              <Badge colorScheme="green" variant="solid" fontSize="md">
                                Avg #{adv.avgPosition.toFixed(1)}
                              </Badge>
                            </HStack>
                            
                            <VStack spacing={3} align="stretch">
                              <Box>
                                <HStack justify="space-between" mb={1}>
                                  <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
                                    <Icon as={FaAd} mr={1} /> Ad Volume
                                  </Text>
                                  <Text fontSize="sm" fontWeight="bold" color={COLORS[idx]}>
                                    {formatAdCount(adv.adCount)} ads
                                  </Text>
                                </HStack>
                                <Progress 
                                  value={adPercent} 
                                  size="sm" 
                                  colorScheme={idx === 0 ? 'blue' : idx === 1 ? 'green' : 'orange'}
                                  borderRadius="full"
                                  bg="gray.200"
                                />
                              </Box>
                              
                              <Box>
                                <HStack justify="space-between" mb={1}>
                                  <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
                                    <Icon as={FaKey} mr={1} /> Keyword Coverage
                                  </Text>
                                  <Text fontSize="sm" fontWeight="bold" color={COLORS[idx]}>
                                    {adv.keywordCount} keywords
                                  </Text>
                                </HStack>
                                <Progress 
                                  value={kwPercent} 
                                  size="sm" 
                                  colorScheme={idx === 0 ? 'blue' : idx === 1 ? 'green' : 'orange'}
                                  borderRadius="full"
                                  bg="gray.200"
                                />
                              </Box>
                            </VStack>
                          </Box>
                        );
                      })}
                      
                      {/* Position comparison summary */}
                      <Box p={3} bg="purple.50" borderRadius="md">
                        <Text fontSize="sm" color="purple.700" fontWeight="medium">
                          <Icon as={FaTrophy} mr={2} />
                          Position Insight: {
                            topAdvertisers.slice(0, 3).reduce((best, adv) => 
                              adv.avgPosition < best.avgPosition ? adv : best
                            ).name.split(' ').slice(0, 3).join(' ')
                          } has the best average position (#{
                            Math.min(...topAdvertisers.slice(0, 3).map(a => a.avgPosition)).toFixed(1)
                          })
                        </Text>
                      </Box>
                    </VStack>
                  ) : (
                    <Text color={subtitleColor}>Need at least 3 advertisers for comparison</Text>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>
          </TabPanel>

          {/* Ad Gallery Tab */}
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              {/* Search and Filters */}
              <HStack spacing={4} flexWrap="wrap">
                <InputGroup maxW="350px">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search ads by title, content, or business..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    bg={cardBg}
                  />
                </InputGroup>
                <InputGroup maxW="300px">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaFilter} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Filter by keyword..."
                    value={keywordFilter}
                    onChange={e => setKeywordFilter(e.target.value)}
                    bg={cardBg}
                  />
                </InputGroup>
                <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                  {filteredAds.length} ads
                </Badge>
              </HStack>

              {/* Ad Cards Grid */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {filteredAds.slice(0, 30).map((ad, index) => (
                  <GoogleAdPreview key={index} ad={ad} />
                ))}
              </SimpleGrid>

              {filteredAds.length > 30 && (
                <Text fontSize="sm" color={subtitleColor} textAlign="center" py={4}>
                  Showing 30 of {filteredAds.length} ads. Use search to narrow results.
                </Text>
              )}
            </VStack>
          </TabPanel>

          {/* Keywords Tab */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Top Keywords */}
              <Card bg={cardBg} shadow="md">
                <CardHeader>
                  <Heading size="md" color={textColor}>
                    <Icon as={FaKey} mr={2} color="green.500" />
                    Top 20 Ad Keywords
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={3} align="stretch">
                    {topKeywords.map((kw, index) => (
                      <HStack key={index} justify="space-between" p={2} bg={statBg} borderRadius="md">
                        <HStack spacing={3}>
                          <Badge colorScheme="blue" variant="solid" w="30px" textAlign="center">
                            {index + 1}
                          </Badge>
                          <Text fontWeight="medium" color={textColor}>{kw.keyword}</Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Badge colorScheme="purple">{formatAdCount(kw.adCount)} ads</Badge>
                          <Badge colorScheme="green">{kw.businessCount} businesses</Badge>
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                </CardBody>
              </Card>

              {/* Common CTAs */}
              <Card bg={cardBg} shadow="md">
                <CardHeader>
                  <Heading size="md" color={textColor}>
                    <Icon as={FaMousePointer} mr={2} color="orange.500" />
                    Popular Call-to-Actions
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Wrap spacing={3}>
                    {commonCTAs.map((cta, index) => (
                      <WrapItem key={index}>
                        <Tag
                          size="lg"
                          colorScheme={index < 3 ? 'orange' : 'gray'}
                          variant={index < 3 ? 'solid' : 'subtle'}
                          py={2}
                          px={4}
                        >
                          <TagLabel textTransform="capitalize" fontWeight="bold">
                            "{cta.phrase}"
                          </TagLabel>
                          <Badge ml={2} colorScheme="blackAlpha" fontSize="xs">
                            {cta.count}
                          </Badge>
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>

                  <Divider my={6} />

                  <VStack spacing={3} align="stretch">
                    <Heading size="sm" color={textColor}>
                      <Icon as={FaInfoCircle} mr={2} />
                      Ad Copy Insights
                    </Heading>
                    <SimpleGrid columns={3} spacing={4}>
                      <Stat size="sm">
                        <StatLabel>Avg Title</StatLabel>
                        <StatNumber>
                          {Math.round(
                            allAds.reduce((sum, ad) => sum + (ad.title?.length || 0), 0) / allAds.length
                          )} chars
                        </StatNumber>
                      </Stat>
                      <Stat size="sm">
                        <StatLabel>Avg Body</StatLabel>
                        <StatNumber>
                          {Math.round(
                            allAds.reduce((sum, ad) => sum + (ad.body?.length || 0), 0) / allAds.length
                          )} chars
                        </StatNumber>
                      </Stat>
                      <Stat size="sm">
                        <StatLabel>Avg Keywords</StatLabel>
                        <StatNumber>
                          {(
                            allAds.reduce((sum, ad) => sum + (ad.keywords?.length || 0), 0) / allAds.length
                          ).toFixed(1)}
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>
          </TabPanel>

          {/* Analytics Tab */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Ad Volume Chart */}
              <Card bg={cardBg} shadow="md">
                <CardHeader>
                  <Heading size="md" color={textColor}>
                    <Icon as={FaChartBar} mr={2} color="blue.500" />
                    Ad Volume by Competitor
                  </Heading>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={adCountByBusiness} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" domain={[0, (dataMax) => Math.ceil(dataMax + 10)]} />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 14, fontWeight: 600 }} />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Box bg="gray.800" p={3} borderRadius="md" boxShadow="lg">
                                <Text color="white" fontWeight="bold">{data.fullName}</Text>
                                <Text color="blue.300">{data.ads} ads</Text>
                                <Text color="green.300">{data.keywords} keywords</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="ads" radius={[0, 8, 8, 0]} label={FavoriteStarBarLabel}>
                        {adCountByBusiness.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>

              {/* Position Distribution */}
              <Card bg={cardBg} shadow="md">
                <CardHeader>
                  <Heading size="md" color={textColor}>
                    <Icon as={FaTrophy} mr={2} color="yellow.500" />
                    Position Distribution
                  </Heading>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={positionDistribution}
                        dataKey="count"
                        nameKey="position"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        innerRadius={60}
                        paddingAngle={2}
                        label={({ position, percent }) => `${position} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {positionDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box bg="gray.800" p={3} borderRadius="md">
                                <Text color="white" fontWeight="bold">
                                  Position {payload[0].name}
                                </Text>
                                <Text color="blue.300">{payload[0].value} ads</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </SimpleGrid>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default AdsDashboard;
